import type { IAdminOrganizationRepository } from "@/application/ports/repositories";
import type {
  AdminOrganization,
  AdminOrganizationUpdateRequest,
  OrgDraftCreateRequest,
  AdminOrganizationMember,
  AdminOrganizationInvitation,
} from "@/domain/admin";
import type {
  AdminEntitlement,
  AdminEntitlementCreateRequest,
  AdminEntitlementUpdateRequest,
  DomainUsageReport,
} from "@/domain/adminUsage";
import type { ModuleCode, OrganizationResponse } from "../generated/types.gen";
import * as sdk from "../generated/sdk.gen";
import { unwrap } from "../unwrap";

function toAdminOrganization(org: OrganizationResponse): AdminOrganization {
  return {
    ...org,
    status: org.is_active ? "active" : "inactive",
  };
}

export class AdminOrganizationRepository implements IAdminOrganizationRepository {
  async getOrganizations(): Promise<AdminOrganization[]> {
    const organizations = await unwrap(sdk.listAllOrganizations());
    return organizations.map(toAdminOrganization);
  }
  async getOrganization(uuid: string): Promise<AdminOrganization> {
    const organization = await unwrap(
      sdk.getOrganizationByUuid({ path: { organization_uuid: uuid } }),
    );
    return toAdminOrganization(organization);
  }
  async createOrganization(data: OrgDraftCreateRequest): Promise<AdminOrganization> {
    const organization = await unwrap(sdk.createOrganization({ body: data }));
    return toAdminOrganization(organization);
  }
  async updateOrganization(
    uuid: string,
    data: AdminOrganizationUpdateRequest,
  ): Promise<AdminOrganization> {
    const organization = await unwrap(
      sdk.adminUpdateOrganization({ path: { organization_uuid: uuid }, body: data }),
    );
    return toAdminOrganization(organization);
  }
  async deleteOrganization(uuid: string): Promise<void> {
    await unwrap(sdk.adminDeleteOrganization({ path: { organization_uuid: uuid } }));
  }
  async getOrganizationMembers(organizationUuid: string): Promise<AdminOrganizationMember[]> {
    return unwrap(sdk.listMembers({ path: { organization_uuid: organizationUuid } }));
  }
  async getOrganizationInvitations(
    organizationUuid: string,
  ): Promise<AdminOrganizationInvitation[]> {
    return unwrap(sdk.listInvitations({ path: { organization_uuid: organizationUuid } }));
  }
  async getOrganizationUsage(
    organizationUuid: string,
    from?: string,
    to?: string,
  ): Promise<DomainUsageReport[]> {
    return unwrap(
      sdk.getOrganizationUsage({
        path: { organization_uuid: organizationUuid },
        query: { from, to },
      }),
    );
  }
  async getOrganizationEntitlements(organizationUuid: string): Promise<AdminEntitlement[]> {
    return unwrap(sdk.listEntitlements({ path: { organization_uuid: organizationUuid } }));
  }
  async grantEntitlement(
    organizationUuid: string,
    data: AdminEntitlementCreateRequest,
  ): Promise<AdminEntitlement> {
    return unwrap(
      sdk.grantEntitlement({ path: { organization_uuid: organizationUuid }, body: data }),
    );
  }
  async updateEntitlement(
    organizationUuid: string,
    moduleCode: string,
    data: AdminEntitlementUpdateRequest,
  ): Promise<AdminEntitlement> {
    return unwrap(
      sdk.updateEntitlement({
        // module codes round-trip from backend entitlement data
        path: { organization_uuid: organizationUuid, module_code: moduleCode as ModuleCode },
        body: data,
      }),
    );
  }
  async resetEntitlementQuota(
    organizationUuid: string,
    moduleCode: string,
  ): Promise<AdminEntitlement> {
    return unwrap(
      sdk.resetEntitlementQuota({
        path: { organization_uuid: organizationUuid, module_code: moduleCode as ModuleCode },
      }),
    );
  }
}
